import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import store from 'store';
import styled from 'styled-components';
import { Link, withPrefix } from 'gatsby';

import Button from '@arcblock/ux/lib/ActionButton';
import FoldIcon from '@material-ui/icons/UnfoldLess';
import UnfoldIcon from '@material-ui/icons/UnfoldMore';

import Group from './group';

const SIDEBAR_NAV_STATE_KEY = 'sidebarNav';

function getSidebarState() {
  return store.get(SIDEBAR_NAV_STATE_KEY) || {};
}

function setSidebarState(state) {
  store.set(SIDEBAR_NAV_STATE_KEY, state);
}

function getId(title) {
  return withPrefix(title);
}

export default class SidebarLinks extends Component {
  constructor(props) {
    super(props);

    const sidebarState = getSidebarState();
    const activeGroup = props.contents.find(this.isGroupSelected);
    if (activeGroup) {
      sidebarState[getId(activeGroup.title)] = true;
      setSidebarState(sidebarState);
    }

    this.state = {
      sidebarState,
    };
  }

  static propTypes = {
    alwaysExpanded: PropTypes.bool,
    contents: PropTypes.array.isRequired,
    pathname: PropTypes.string.isRequired,
    onToggleAll: PropTypes.func,
    onToggleGroup: PropTypes.func,
  };

  static defaultProps = {
    alwaysExpanded: false,
    onToggleAll: () => {},
    onToggleGroup: () => {},
  };

  get allExpanded() {
    return this.props.contents.every(({ title }) => this.state.sidebarState[getId(title)]);
  }

  isPageSelected = ({ path }) => {
    // prettier-ignore
    const [prefixedPath, pathname] = [withPrefix(path), this.props.pathname].map(x =>
      x.replace(/\/$/, ''));
    return pathname.startsWith(prefixedPath);
  };

  isGroupSelected = ({ path, pages }) => {
    const result = path ? this.isPageSelected({ path }) : pages.some(this.isPageSelected);
    return result;
  };

  toggleGroup = title => {
    this.setState(prevState => {
      const id = getId(title);
      const expanded = !prevState.sidebarState[id];
      const sidebarState = {
        ...getSidebarState(),
        [id]: expanded,
      };

      if (this.props.onToggleGroup) {
        this.props.onToggleGroup(title, expanded);
      }

      setSidebarState(sidebarState);
      return { sidebarState };
    });
  };

  toggleAll = () => {
    const expanded = !this.allExpanded;
    const sidebarState = this.props.contents.reduce(
      (acc, { title }) => ({
        ...acc,
        [getId(title)]: expanded,
      }),
      {}
    );

    setSidebarState(sidebarState);
    this.setState({ sidebarState });
    if (this.props.onToggleAll) {
      this.props.onToggleAll(expanded);
    }
  };

  render() {
    const Icon = this.allExpanded ? FoldIcon : UnfoldIcon;
    return (
      <Div>
        {this.props.contents.length >= 2 && (
          <div className="button-wrapper">
            <Button onClick={this.toggleAll} size="small" tabIndex={-1}>
              <Icon size={18} />
              {this.allExpanded ? 'Collapse' : 'Expand'} all
            </Button>
          </div>
        )}
        {this.props.contents.map(({ title, url, path, pages }, index) => {
          const contents = this.renderPages(pages);
          if (!title) {
            return <Fragment key="root">{contents}</Fragment>;
          }

          const selected = this.isGroupSelected({ pages, path: path || url });
          const expanded = Boolean(this.state.sidebarState[getId(title)]);

          return (
            <Group
              key={title}
              title={title}
              path={path}
              url={url}
              isFirst={!index}
              expanded={this.props.alwaysExpanded || expanded || selected}
              active={selected}
              onClick={this.props.alwaysExpanded ? null : this.toggleGroup}>
              {contents}
            </Group>
          );
        })}
      </Div>
    );
  }

  renderPages(pages) {
    return (
      <StyledList>
        {pages.map(page => (
          <StyledListItem key={page.path} active={this.isPageSelected(page)}>
            {page.anchor ? (
              <a href={page.path}>{page.title}</a>
            ) : (
              <Link className={this.isPageSelected(page) ? 'active' : null} to={page.path}>
                {page.title}
              </Link>
            )}
          </StyledListItem>
        ))}
      </StyledList>
    );
  }
}

const Div = styled.div`
  .button-wrapper {
    position: fixed;
    background: ${props => props.theme.palette.background.default};
    bottom: 0;
    width: 260px;
    padding: 24px 0;
    .action-button {
      width: 250px;
    }
  }
`;

const StyledList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const StyledListItem = styled.li`
  font-size: 1rem;
  line-height: 2.4rem;
  a {
    color: ${props => (props.active ? props.theme.colors.blue : 'inherit')};
    font-weight: ${props => (props.active ? 500 : 'normal')};
    text-decoration: none;
    &:hover {
      opacity: 0.8;
    }
  }

  &:last-of-type {
    margin-bottom: 8px;
  }
`;
